import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DownloadSimple, FileXls, User as UserIcon } from "@phosphor-icons/react";

/**
 * Hours Report page.
 *
 * Two modes:
 *   • Roster — weekly / monthly aggregate for every member (sortable by hours).
 *   • Individual — deep-dive on a single member with a per-tour breakdown and
 *     a per-member PDF export.
 */
export default function Reports({ embedded = false }) {
  const [mode, setMode] = useState("roster"); // "roster" | "individual"
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [pickedUser, setPickedUser] = useState("");
  const [indData, setIndData] = useState(null);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  useEffect(() => {
    api.get("/users/").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  async function loadRoster() {
    if (period === "monthly") {
      const { data } = await api.get(`/reports/monthly?year=${year}&month=${month}`);
      setData(data);
    } else {
      const { data } = await api.get(`/reports/weekly`);
      setData(data);
    }
  }
  async function loadIndividual() {
    if (!pickedUser) { setIndData(null); return; }
    const { data } = await api.get(`/reports/individual/${pickedUser}?year=${year}&month=${month}`);
    setIndData(data);
  }

  useEffect(() => {
    if (mode === "roster") loadRoster();
    else loadIndividual();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, period, year, month, pickedUser]);

  const rows = data?.rows || [];
  const threshold = data?.threshold ?? 20;
  const sortedUsers = useMemo(() => {
    const key = (u) => {
      const parts = String(u.name || "").trim().split(/\s+/);
      return (parts[parts.length - 1] || parts[0] || "").toLowerCase();
    };
    return [...users].sort((a, b) => key(a).localeCompare(key(b)));
  }, [users]);

  const controls = (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="border border-slate-200 rounded-sm overflow-hidden flex" data-testid="report-mode">
        <button onClick={() => setMode("roster")} className={`px-3 py-1.5 text-xs font-semibold ${mode === "roster" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`} data-testid="mode-roster">ROSTER</button>
        <button onClick={() => setMode("individual")} className={`px-3 py-1.5 text-xs font-semibold ${mode === "individual" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`} data-testid="mode-individual">INDIVIDUAL</button>
      </div>

      {mode === "roster" && (
        <div className="border border-slate-200 rounded-sm overflow-hidden flex">
          <button onClick={() => setPeriod("weekly")} className={`px-3 py-1.5 text-xs font-semibold ${period === "weekly" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`} data-testid="period-weekly">WEEKLY</button>
          <button onClick={() => setPeriod("monthly")} className={`px-3 py-1.5 text-xs font-semibold ${period === "monthly" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`} data-testid="period-monthly">MONTHLY</button>
        </div>
      )}

      {(mode === "individual" || (mode === "roster" && period === "monthly")) && (
        <>
          <select value={year} onChange={(e) => setYear(+e.target.value)} data-testid="report-year" className="border border-slate-200 rounded-sm h-9 px-2 text-sm">
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <option key={y}>{y}</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(+e.target.value)} data-testid="report-month" className="border border-slate-200 rounded-sm h-9 px-2 text-sm">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString(undefined, { month: "long" })}</option>)}
          </select>
        </>
      )}

      {mode === "roster" && period === "monthly" && (
        <>
          <Button asChild variant="outline" className="rounded-sm" data-testid="report-excel">
            <a href={`${API_BASE}/reports/monthly/excel?year=${year}&month=${month}`} target="_blank" rel="noreferrer"><FileXls size={14} weight="bold" className="mr-1" />EXCEL</a>
          </Button>
          <Button asChild className="rounded-sm bg-slate-900 text-white" data-testid="report-pdf">
            <a href={`${API_BASE}/reports/monthly/pdf?year=${year}&month=${month}`} target="_blank" rel="noreferrer"><DownloadSimple size={14} weight="bold" className="mr-1" />PDF</a>
          </Button>
        </>
      )}
      {mode === "roster" && period === "weekly" && (
        <Button asChild variant="outline" className="rounded-sm" data-testid="report-excel-weekly">
          <a href={`${API_BASE}/reports/weekly/excel`} target="_blank" rel="noreferrer"><FileXls size={14} weight="bold" className="mr-1" />EXCEL</a>
        </Button>
      )}
      {mode === "individual" && pickedUser && (
        <Button asChild className="rounded-sm bg-slate-900 text-white" data-testid="report-individual-pdf">
          <a href={`${API_BASE}/reports/individual/${pickedUser}/pdf?year=${year}&month=${month}`} target="_blank" rel="noreferrer">
            <DownloadSimple size={14} weight="bold" className="mr-1" />MEMBER PDF
          </a>
        </Button>
      )}
    </div>
  );

  const body = (
    <>
      {mode === "roster" && <RosterView rows={rows} threshold={threshold} />}
      {mode === "individual" && (
        <IndividualView
          users={sortedUsers}
          pickedUser={pickedUser}
          onPick={setPickedUser}
          data={indData}
          year={year}
          month={month}
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-slate-500">{mode === "roster" ? "Members under threshold are highlighted" : "Detailed per-member breakdown with PDF export"}</p>
          {controls}
        </div>
        {body}
      </div>
    );
  }

  return (
    <AppLayout
      title="Volunteer Hours Report"
      subtitle={mode === "roster" ? "Members under threshold are highlighted" : "Detailed per-member breakdown with PDF export"}
      actions={controls}
    >
      {body}
    </AppLayout>
  );
}

function RosterView({ rows, threshold }) {
  const active = rows.filter((r) => !r.loa).slice().sort((a, b) => (b.hours || 0) - (a.hours || 0));
  const loa = rows.filter((r) => r.loa);
  return (
    <div className="surface-grid">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="font-display font-bold">Hours Summary</div>
        <div className="mono text-xs">Threshold: <span className="font-semibold text-slate-900">{threshold} hrs</span></div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-white border-b border-slate-200">
          <tr className="text-left tab-tag text-slate-500">
            <th className="px-4 py-2">Member</th><th className="px-4 py-2">Role</th><th className="px-4 py-2">Certification</th><th className="px-4 py-2 text-right">Hours</th><th className="px-4 py-2 text-right">Tours</th><th className="px-4 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={6} className="px-4 py-6 text-slate-500">No data.</td></tr>
          ) : (
            <>
              {active.map((r) => {
                const under = r.role === "volunteer" && r.hours < threshold;
                return (
                  <tr key={r.id} className={`border-b border-slate-100 ${under ? "bg-red-50" : "hover:bg-slate-50"}`} data-testid={`report-row-${r.id}`}>
                    <td className={`px-4 py-2 font-medium ${under ? "text-red-700" : "text-slate-900"}`}>{r.name}</td>
                    <td className="px-4 py-2 mono text-xs uppercase">{r.role}</td>
                    <td className="px-4 py-2"><Badge className="rounded-sm bg-slate-700 text-white">{r.certification}</Badge></td>
                    <td className={`px-4 py-2 text-right mono font-semibold ${under ? "text-red-700" : ""}`}>{r.hours.toFixed(1)}</td>
                    <td className="px-4 py-2 text-right mono">{r.tours}</td>
                    <td className="px-4 py-2 tab-tag">{under ? <span className="text-red-700">UNDER THRESHOLD</span> : <span className="text-emerald-700">OK</span>}</td>
                  </tr>
                );
              })}
              {loa.length > 0 && (
                <>
                  <tr data-testid="report-loa-header">
                    <td colSpan={6} className="px-4 py-2 bg-slate-200 border-t-4 border-slate-400 tab-tag text-slate-700 font-bold">
                      Leave of Absence · Not counted toward hour totals
                    </td>
                  </tr>
                  {loa.map((r) => (
                    <tr key={r.id} className="bg-slate-100 border-b border-slate-200 italic text-slate-600" data-testid={`report-loa-row-${r.id}`}>
                      <td className="px-4 py-2 font-medium">{r.name}</td>
                      <td className="px-4 py-2 mono text-xs uppercase">{r.role}</td>
                      <td className="px-4 py-2"><Badge className="rounded-sm bg-slate-500 text-white">{r.certification}</Badge></td>
                      <td className="px-4 py-2 text-right mono font-bold text-slate-500">LOA</td>
                      <td className="px-4 py-2 text-right mono">—</td>
                      <td className="px-4 py-2 tab-tag text-slate-500">ON LOA · until {r.loa.end_date}</td>
                    </tr>
                  ))}
                </>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

function IndividualView({ users, pickedUser, onPick, data, year, month }) {
  const monthLabel = new Date(year, month - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
  return (
    <div className="space-y-4">
      <div className="surface-grid p-4 flex items-center gap-3 flex-wrap" data-testid="individual-picker">
        <UserIcon size={18} weight="bold" className="text-slate-500" />
        <select
          value={pickedUser}
          onChange={(e) => onPick(e.target.value)}
          className="border border-slate-200 rounded-sm h-9 px-2 text-sm min-w-[280px]"
          data-testid="individual-user-select"
        >
          <option value="">Select a member…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
        </select>
        <span className="text-xs text-slate-500">Period: <strong className="text-slate-900">{monthLabel}</strong></span>
      </div>

      {!pickedUser && (
        <div className="surface-grid p-8 text-center text-slate-500" data-testid="individual-empty">
          Pick a member above to see their tours and hours breakdown.
        </div>
      )}

      {pickedUser && data && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Member" value={data.user?.name || "—"} sub={data.user?.role} />
            <StatCard label="Total Hours" value={(data.total_hours ?? 0).toFixed(1)} sub={monthLabel} highlight />
            <StatCard label="Tours" value={data.tour_count ?? 0} sub={monthLabel} />
          </div>

          <div className="surface-grid">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 font-display font-bold">Tour Log</div>
            <table className="w-full text-sm">
              <thead className="bg-white border-b border-slate-200">
                <tr className="text-left tab-tag text-slate-500">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Position</th>
                  <th className="px-4 py-2">Role</th>
                  <th className="px-4 py-2">Start – End</th>
                  <th className="px-4 py-2 text-right">Hours</th>
                </tr>
              </thead>
              <tbody>
                {(data.tours || []).length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-6 text-slate-500">No tours for this period.</td></tr>
                ) : (data.tours || []).map((t) => {
                  const start = new Date(t.start_time);
                  const end = new Date(t.end_time);
                  const hrs = (end - start) / 3600000;
                  return (
                    <tr key={t.id} className="border-b border-slate-100" data-testid={`individual-tour-${t.id}`}>
                      <td className="px-4 py-2 mono">{start.toLocaleDateString()}</td>
                      <td className="px-4 py-2 uppercase font-medium">{t.position || t.title || "—"}</td>
                      <td className="px-4 py-2"><Badge className="rounded-sm bg-slate-700 text-white text-[10px]">{t.ambulance_role || "—"}</Badge></td>
                      <td className="px-4 py-2 mono text-xs">{start.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })} – {end.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}</td>
                      <td className="px-4 py-2 text-right mono font-semibold">{hrs.toFixed(1)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, highlight }) {
  return (
    <div className={`surface-grid p-4 ${highlight ? "bg-emerald-50 border-emerald-300" : ""}`}>
      <div className="tab-tag text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-2xl font-bold ${highlight ? "text-emerald-800" : "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">{sub}</div>}
    </div>
  );
}
